import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { appSettings } from "../../app/appSettingsSlice";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import "./../order/ManageOrderFilters.css";

const SalesBreakdownByTime = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const user = useSelector((state) => state.auth.user);
  const { profileData } = useSelector((state) => state.auth);
  const userIdentity = user?.userData?.userId ?? user?.userData?.email ?? CompID;
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "20", label: "20" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Date range state - now handled by Redux

  // Additional dropdown states
  const [locations, setLocations] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Column selection and preferences
  const pageKey = "salesBreakdownByTime";
  const defaultVisibleColumns = {
    transactionQty: true,
    refundQty: true,
    refundValue: true,
    itemQty: true,
    salesIncTax: true,
    discountsPromotions: true,
    serviceCharges: true,
    extraCharges: true,
    tipAmount: true,
    avgTransactionNetSales: true,
    netSalesIncTax: true,
    netSalesExcTax: true
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "hourCommencing", label: "Time Period" };
  
  // Default filter: "All Locations" so reports show all branches unless user selects one
  const parentIdForLocation = CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultStaff = { value: "all", label: "All Staff" };
  const defaultTimeInterval = { value: "hourly", label: "Hourly" };
  const defaultDevice = { value: "all", label: "All Devices" };
  
  // Default to "This Week" if no existing filter
  const defaultDateRange = [
    moment().startOf('week').toDate(),
    moment().endOf('week').toDate(),
  ];
  
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  
  // Get filter values from Redux
  const location = preferences.location || defaultLocation;
  const staff = preferences.staff || defaultStaff;
  const timeInterval = preferences.timeInterval || defaultTimeInterval;
  const device = preferences.device || defaultDevice;
  // Coerce cleared/null dates back to default "This Week"
  const dateRange = (() => {
    const stored = preferences.dateRange;
    if (!Array.isArray(stored)) return defaultDateRange;
    const startOk = stored[0] != null && moment(stored[0]).isValid();
    const endOk = stored[1] != null && moment(stored[1]).isValid();
    if (!startOk && !endOk) return defaultDateRange;
    return [
      startOk ? stored[0] : defaultDateRange[0],
      endOk ? stored[1] : defaultDateRange[1],
    ];
  })();
  
  // Get raw dates from Redux
  const [rawStartDate, rawEndDate] = dateRange;
  const dispatch = useDispatch();

  // Remove old filter state - now using Redux

  // Get app settings from Redux
  // const appSettings = useSelector((state) => state.appSettings);
  // const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Currency symbols mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Format date with day name for Daily and Hourly intervals
  const formatTimePeriod = (date, interval) => {
    const dateMoment = moment(date);
    const intervalValue = interval?.value || interval || 'hourly';
    
    if (intervalValue === 'daily') {
      // Format: "Monday, 08/12/2025"
      return dateMoment.format('dddd, DD/MM/YYYY');
    } else if (intervalValue === 'hourly') {
      // Format: "Monday, 08/12/2025 00:00"
      return dateMoment.format('dddd, DD/MM/YYYY HH:mm');
    } else {
      // For weekly and monthly, use standard format
      return dateMoment.format('DD/MM/YYYY HH:mm');
    }
  };

  // Function to get theme color
  // const getThemeColor = () => {
  //   const colorMap = {
  //     'red': '#dc3545',
  //     'pink': '#e91e63',
  //     'orange': '#fd7e14',
  //     'yellow': '#ffc107',
  //     'lime': '#84cc16',
  //     'green': '#198754',
  //     'teal': '#20c997',
  //     'cyan': '#0dcaf0',
  //     'blue': '#0d6efd',
  //     'purple': '#6f42c1',
  //     'indigo': '#6610f2',
  //     'dark': '#212529'
  //   };
  //   return colorMap[appTheme] || '#20c997'; // Default to teal
  // };

  

  // Filter options: "All Locations" first, then parent + branches
  const allLocationsOption = { value: "all", label: "All Locations" };
  const locationOptions =
    locations.length > 0
      ? [allLocationsOption, ...locations]
      : parentIdForLocation != null && !isNaN(parentIdForLocation)
        ? [allLocationsOption, { value: parentIdForLocation, label: companyName }]
        : [allLocationsOption];

  const staffOptions = staffData.length > 0 ? staffData : [
    { value: "all", label: "All Staff" }
  ];

  const timeIntervalOptions = [
    { value: "hourly", label: "Hourly" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" }
  ];

  const deviceOptions = devices.length > 0 ? devices : [
    { value: "all", label: "All Devices" }
  ];


  // Sort options
  const sortByOptions = [
    { value: "hourCommencing", label: "Time Period" },
    { value: "transactionQty", label: "Orders" },
    { value: "salesIncTax", label: "Sales Inc. Tax" },
    { value: "netSalesIncTax", label: "Net Sales Inc. Tax" }
  ];

  const sortOrderOptions = [
    { value: "ASC", label: "ASC" },
    { value: "DESC", label: "DESC" }
  ];

  // Sample data for demonstration (removed highlighted columns)
  const sampleData = [
    {
      hourCommencing: "01/10/2025 13:00:00",
      transactionQty: 1,
      refundQty: 0,
      refundValue: 0.00,
      itemQty: 6,
      salesIncTax: 39.45,
      discountsPromotions: 3.95,
      avgTransactionNetSales: 35.50,
      netSalesIncTax: 35.50,
      netSalesExcTax: 29.60
    },
    {
      hourCommencing: "30/09/2025 22:00:00",
      transactionQty: 6,
      refundQty: 0,
      refundValue: 0.00,
      itemQty: 117,
      salesIncTax: 461.88,
      discountsPromotions: 0.00,
      avgTransactionNetSales: 76.98,
      netSalesIncTax: 461.88,
      netSalesExcTax: 454.63
    },
    {
      hourCommencing: "30/09/2025 21:00:00",
      transactionQty: 3,
      refundQty: 1,
      refundValue: 15.50,
      itemQty: 45,
      salesIncTax: 125.30,
      discountsPromotions: 5.00,
      avgTransactionNetSales: 41.77,
      netSalesIncTax: 125.30,
      netSalesExcTax: 104.42
    },
    {
      hourCommencing: "30/09/2025 20:00:00",
      transactionQty: 8,
      refundQty: 0,
      refundValue: 0.00,
      itemQty: 89,
      salesIncTax: 298.75,
      discountsPromotions: 12.50,
      avgTransactionNetSales: 37.34,
      netSalesIncTax: 298.75,
      netSalesExcTax: 248.96
    },
    {
      hourCommencing: "30/09/2025 19:00:00",
      transactionQty: 12,
      refundQty: 2,
      refundValue: 25.00,
      itemQty: 156,
      salesIncTax: 445.20,
      discountsPromotions: 20.00,
      avgTransactionNetSales: 37.10,
      netSalesIncTax: 445.20,
      netSalesExcTax: 371.00
    }
  ];

  // Handle filter changes with Redux
  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
    setPageNumber(1);
  };

  // Reset filters with Redux
  const resetFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    dispatch(setFilterField({ pageKey, field: "timeInterval", value: defaultTimeInterval }));
    dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    setSearch("");
  };

  // Clear all filters with automatic filtering
  const clearAllFilters = () => {
    resetFilters();
    setPageNumber(1);
    // Trigger automatic filtering after clearing
    setTimeout(() => {
      applyFilters();
    }, 100);
  };

  // Handle search with automatic filtering
  const handleSearch = () => {
    setPageNumber(1);
    applyFilters();
  };

  // Handle search input change with automatic filtering
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPageNumber(1);
    // Trigger automatic filtering after a short delay
    setTimeout(() => {
      applyFilters();
    }, 300); // Slightly longer delay for search to avoid too many calls
  };

  // Apply filters with debounce to prevent multiple rapid calls.
  // Time handling: Backend filters raw orders by Recvorder_datetime in [startDate, endDate],
  // then groups by interval. Hourly: each order is put in the bucket for its hour (e.g. 1:35 PM → 1:00 PM bucket).
  // So if user selects start 1:35 PM, the "1:00 PM" row contains only orders from 1:35–1:59 (partial hour), not 1:00–1:34.
  const applyFilters = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = rawStartDate ? (typeof rawStartDate === 'string' ? new Date(rawStartDate) : rawStartDate) : null;
      const endDate = rawEndDate ? (typeof rawEndDate === 'string' ? new Date(rawEndDate) : rawEndDate) : null;

      const defaultStartDate = startDate || defaultDateRange[0];
      const defaultEndDate = endDate || defaultDateRange[1];

      const formattedStartDate = moment(defaultStartDate).format('YYYY-MM-DD HH:mm:ss');
      const formattedEndDate = moment(defaultEndDate).format('YYYY-MM-DD HH:mm:ss');
      
      const params = {
        CompID,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        locationFilter: isMain && location.value !== 'all' && location.value != null ? (typeof location.value === 'number' ? location.value : parseInt(location.value)) : null,
        staffFilter: staff.value !== 'all' ? staff.value : null,
        deviceFilter: device.value !== 'all' ? device.value : null,
        timeInterval: timeInterval.value,
        searchTerm: search || null,
        sortBy: sortBy.value,
        sortOrder: sortOrder.value,
        pageNumber: pageNumber,
        pageSize: pageSize.value
      };
      
      console.log('API Parameters:', params); // Debug log

      const response = await axiosInstance.get(APIs.GET_SALES_BREAKDOWN_BY_TIME, { params });
      
      console.log('API Response:', response.data); // Debug log
      
      if (response.data.status) {
        const responseData = response.data.data;
        console.log('Response Data:', responseData); // Debug log
        
        // Handle different response structures
        const salesDataRaw = responseData?.SalesData ?? responseData?.salesData ?? responseData ?? [];
        const firstRow = Array.isArray(salesDataRaw) && salesDataRaw.length > 0 ? salesDataRaw[0] : null;
        const parsedPageSize = Number(pageSize?.value ?? pageSize) || 20;

        // Totals can come from root/data/row depending on backend shape (notably for All Locations)
        const totalCount = Number(
          responseData?.TotalCount ??
          responseData?.totalCount ??
          responseData?.TotalRecords ??
          responseData?.totalRecords ??
          response.data?.TotalCount ??
          response.data?.totalCount ??
          response.data?.TotalRecords ??
          response.data?.totalRecords ??
          firstRow?.TotalCount ??
          firstRow?.totalCount ??
          firstRow?.TotalRecords ??
          firstRow?.totalRecords ??
          0
        );
        const totalPagesFromResponse = Number(
          responseData?.TotalPages ??
          responseData?.totalPages ??
          response.data?.TotalPages ??
          response.data?.totalPages ??
          firstRow?.TotalPages ??
          firstRow?.totalPages ??
          0
        );
        const totalPages = totalPagesFromResponse > 0
          ? totalPagesFromResponse
          : Math.ceil((Number.isFinite(totalCount) ? totalCount : 0) / parsedPageSize);
        
        // Normalize property names to camelCase (in case backend sends PascalCase)
        const salesData = Array.isArray(salesDataRaw) ? salesDataRaw.map(item => ({
          hourCommencing: item.hourCommencing || item.HourCommencing,
          transactionQty: item.transactionQty || item.TransactionQty || 0,
          refundQty: item.refundQty || item.RefundQty || 0,
          refundValue: item.refundValue || item.RefundValue || 0,
          itemQty: item.itemQty || item.ItemQty || 0,
          salesIncTax: item.salesIncTax || item.SalesIncTax || 0,
          discountsPromotions: item.discountsPromotions || item.DiscountsPromotions || 0,
          serviceCharges: item.serviceCharges || item.ServiceCharges || 0,
          extraCharges: item.extraCharges || item.ExtraCharges || 0,
          tipAmount: item.tipAmount || item.TipAmount || 0,
          avgTransactionNetSales: item.avgTransactionNetSales || item.AvgTransactionNetSales || 0,
          netSalesIncTax: item.netSalesIncTax || item.NetSalesIncTax || 0,
          netSalesExcTax: item.netSalesExcTax || item.NetSalesExcTax || 0
        })) : [];
        
        console.log('Sales Data (normalized):', salesData); // Debug log
        console.log('Total Count:', totalCount); // Debug log
        console.log('Total Pages:', totalPages); // Debug log
        
        setSalesData(salesData);
        setTotalRecords(Number.isFinite(totalCount) ? totalCount : 0);
        setTotalPages(Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1);
        
        // Show success message if we have data
        if (salesData.length > 0) {
          console.log(`Loaded ${salesData.length} sales records`);
        } else {
          console.log('No sales data found for the selected criteria');
        }
      } else {
        console.log('API Error:', response.data.message); // Debug log
        showSuccessToast("Error loading sales data");
        setSalesData([]);
        setTotalRecords(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
      showSuccessToast("Error loading sales data");
      setSalesData([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [CompID, rawStartDate, rawEndDate, location, staff, timeInterval, device, search, sortBy, sortOrder, pageNumber, pageSize]);




  // Fetch locations (company branches): company name + branch names only (no "All Locations")
  const fetchLocations = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";

      const base = [{ value: parentId, label: compName }];

      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          if (pidNum === parentId && idNum !== parentId) return true;
          return false;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);

      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showSuccessToast("Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, profileData?.compName, profileData?.CompName]);

  // Fetch staff (excluding riders)
  const fetchStaff = useCallback(async () => {
    try {
      setLoadingStaff(true);
      
      // Fetch staff list
      const staffResponse = await axiosInstance.get(APIs.GET_ALL_STAFF, {
        params: {
          CompID
        }
      });
      const staffData = staffResponse.data.data || staffResponse.data || [];

      // Fetch roles to identify riders
      let riderRoleIds = new Set();
      try {
        const rolesResponse = await axiosInstance.get(APIs.GET_ROLES, {
          params: {
            CompID
          }
        });
        const rolesData = rolesResponse.data.data || rolesResponse.data || [];
        // Find all role IDs with "Rider" title (case-insensitive)
        rolesData.forEach(role => {
          if (role.r_Title && role.r_Title.toLowerCase() === 'rider') {
            riderRoleIds.add(role.comRolePermission_ID);
          }
        });
      } catch (roleError) {
        console.error('Error fetching roles:', roleError);
        // Continue without role filtering if roles can't be fetched
      }

      // Filter out staff with rider role
      const filteredStaff = staffData.filter(staffMember => {
        // Exclude if staff has a rider role
        const roleId = staffMember.comRolePermission_ID || staffMember.ComRolePermission_ID || staffMember.userRoles?.comRolePermission_ID;
        return !riderRoleIds.has(roleId);
      });

      // Transform staff to dropdown format
      const staffOptions = [
        { value: "all", label: "All Staff" },
        ...filteredStaff.map(staffMember => ({
          value: staffMember.userName,
          label: `${staffMember.firstName || staffMember.FirstName || ''} ${staffMember.lastName || staffMember.LastName || ''}`.trim()  
        }))
      ];

      setStaffData(staffOptions);
    } catch (error) {
      console.error('Error fetching staff:', error);
      showSuccessToast(error.response?.data?.message || "Error loading staff");
    } finally {
      setLoadingStaff(false);
    }
  }, [CompID]);

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    try {
      setLoadingDevices(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DEVICES, {
        params: {
          CompID
        }
      });
      const deviceData = response.data.data || response.data || [];

      // Transform devices to dropdown format
      const deviceOptions = [
        { value: "all", label: "All Devices" },
        ...deviceData.map(device => ({
          value: device.deviceID,
          label: device.deviceName
        }))
      ];

      setDevices(deviceOptions);
    } catch (error) {
      console.error('Error fetching devices:', error);
      showSuccessToast("Error loading devices");
    } finally {
      setLoadingDevices(false);
    }
  }, [CompID]);

  // Handle date range changes (preserves date + time from picker when using Custom Date with time)
  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, null];
    // Picker clear → restore default "This Week" (same as Clear filters)
    if (
      (!newStartDate || !moment(newStartDate).isValid()) &&
      (!newEndDate || !moment(newEndDate).isValid())
    ) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
      setPageNumber(1);
      return;
    }
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      let effectiveStartDate = newStartDate;
      let effectiveEndDate = newEndDate;
      if (newStartDate && newEndDate && newStartDate > newEndDate) {
        [effectiveStartDate, effectiveEndDate] = [newEndDate, newStartDate];
      } else if (newStartDate && !newEndDate) {
        effectiveEndDate = new Date(newStartDate.getTime());
        effectiveEndDate.setHours(23, 59, 59, 999);
      }
      dispatch(
        setFilterField({
          pageKey,
          field: "dateRange",
          value: [effectiveStartDate, effectiveEndDate],
        })
      );
      setPageNumber(1);
    } else {
      showSuccessToast("Invalid date selected");
    }
  };

  // Refresh data
  const refreshData = () => {
    applyFilters();
  };

  // Initialize preferences
  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    
    // Initialize filter fields
    if (!preferences.location) {
      dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    }
    if (!preferences.staff) {
      dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    }
    if (!preferences.timeInterval) {
      dispatch(setFilterField({ pageKey, field: "timeInterval", value: defaultTimeInterval }));
    }
    if (!preferences.device) {
      dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    }
    if (!preferences.dateRange) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    }
  }, [dispatch, pageKey]);

  // Track if component has mounted to prevent initial double load
  const [hasMounted, setHasMounted] = useState(false);

  // Load initial data only once on component mount
  useEffect(() => {
    applyFilters();
    setHasMounted(true);
  }, []); // Remove applyFilters from dependencies to prevent multiple calls

  // Watch for filter changes and apply filters (but not on initial mount)
  useEffect(() => {
    if (hasMounted) {
      applyFilters();
    }
  }, [timeInterval, location, staff, device, rawStartDate, rawEndDate, pageNumber, pageSize.value, sortBy, sortOrder]);

  // Fetch all dropdown data on component mount; locations only for parent company
  useEffect(() => {
    if (isMain) fetchLocations();
    fetchStaff();
    fetchDevices();
  }, [isMain, fetchLocations, fetchStaff, fetchDevices]);

  // When user/account changes (login, logout+login as different account), reset location and refetch so data loads without refresh
  useEffect(() => {
    if (!CompID) return;
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    if (isMain) fetchLocations();
  }, [userIdentity, CompID, isMain, dispatch, pageKey]);

  return (
    <div>
      {loading && <Preloader />}

      {/* Breadcrumb */}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Sales Breakdown by Time</li>
          </ol>
          <h1 className="page-header mb-0">Sales Breakdown by Time</h1>
        </div>
      </div>

      {/* Filter Bar - Top Section */}
      <div 
        className="card mb-4"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: '12px',
          border: 'none',
          boxShadow: `0 4px 20px ${themeColor}40`
        }}
      >
        <div className="card-body p-3">

          {/* Date Range and Search Section */}
          <div className="row g-3 align-items-center mb-3">

            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="filter-control">
                <div
                  style={{
                    position: 'relative',
                    zIndex: 9999,
                    width: '100%'
                  }}
                >
                  <DateRangePicker
                    startDate={rawStartDate ? (typeof rawStartDate === 'string' ? new Date(rawStartDate) : rawStartDate) : null}
                    endDate={rawEndDate ? (typeof rawEndDate === 'string' ? new Date(rawEndDate) : rawEndDate) : null}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                    className="filter-dropdown"
                    allowSingleDate={true}
                    allowDateRange={true}
                    showTimeSelection={true}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-lg-4 col-md-6">
              <div className="search-input filter-control">
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by name, type, category..."
                  value={search}
                  onChange={handleSearchChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: '#333'
                  }}
                />
              </div>
            </div>


            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="filter-control">
                <Select
                  value={staff}
                  onChange={(option) => handleFilterChange('staff', option)}
                  options={staffOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingStaff}
                  placeholder="Staff"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>

         

            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="clear-btn"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  <i className="fa fa-times me-1"></i>
                  Filter
                </button>

                <ColumnSelection
                  pageKey={pageKey}
                  visibleColumns={visibleColumns}
                  defaultVisibleColumns={defaultVisibleColumns}
                  onColumnsChange={(columns) => dispatch(setVisibleColumns({ pageKey, columns }))}
                />

              </div>
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className="row g-3 align-items-center">
            {isMain && locationOptions.length > 1 && (
              <div className="col-xl-2 col-lg-4 col-md-6">
                <div className="filter-control">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange('location', option)}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="Location"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll={false}
                    menuShouldScrollIntoView={false}
                    closeMenuOnScroll={false}
                  />
                </div>
              </div>
            )}

            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={device}
                  onChange={(option) => handleFilterChange('device', option)}
                  options={deviceOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingDevices}
                  placeholder="Device"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={timeInterval}
                  onChange={(option) => handleFilterChange('timeInterval', option)}
                  options={timeIntervalOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  placeholder="Time Interval"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Data Table */}
      <div className="card" style={{ position: 'relative', zIndex: 1 }}>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead style={{ position: 'sticky', top: 0, zIndex: 5, backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa' }}>
                <tr>
                  <th>TIME PERIOD</th>
                  {visibleColumns.transactionQty && <th>ORDERS</th>}
                  {visibleColumns.itemQty && <th>ITEM QTY</th>}
                  {visibleColumns.salesIncTax && <th>SALES INC. TAX</th>}
                  {visibleColumns.discountsPromotions && <th>DISCOUNTS</th>}
                  {visibleColumns.serviceCharges && <th>SERVICE CHARGES</th>}
                  {visibleColumns.extraCharges && <th>EXTRA CHARGES</th>}
                  {visibleColumns.tipAmount && <th>TIP AMOUNT</th>}
                  {visibleColumns.avgTransactionNetSales && <th>AVG NET SALES</th>}
                  {visibleColumns.netSalesIncTax && <th>NET SALES INC. TAX</th>}
                  {visibleColumns.netSalesExcTax && <th>NET SALES EXC. TAX</th>}
                  {visibleColumns.refundQty && <th>REFUND QTY</th>}
                  {visibleColumns.refundValue && <th>REFUND VALUE</th>}
                </tr>
              </thead>
              <tbody>
                {salesData.map((row, index) => (
                  <tr key={index}>
                    <td>{formatTimePeriod(row.hourCommencing, timeInterval)}</td>
                    {visibleColumns.transactionQty && <td>{row.transactionQty}</td>}
                    {visibleColumns.itemQty && <td>{row.itemQty}</td>}
                    {visibleColumns.salesIncTax && <td style={{ color: '#0d6efd' }}>{getCurrencySymbol(compCurrency)} {row.salesIncTax.toFixed(2)}</td>}
                    {visibleColumns.discountsPromotions && <td>{getCurrencySymbol(compCurrency)} {row.discountsPromotions.toFixed(2)}</td>}
                    {visibleColumns.serviceCharges && <td>{getCurrencySymbol(compCurrency)} {(row.serviceCharges || 0).toFixed(2)}</td>}
                    {visibleColumns.extraCharges && <td>{getCurrencySymbol(compCurrency)} {(row.extraCharges || 0).toFixed(2)}</td>}
                    {visibleColumns.tipAmount && <td>{getCurrencySymbol(compCurrency)} {(row.tipAmount || 0).toFixed(2)}</td>}
                    {visibleColumns.avgTransactionNetSales && <td>{getCurrencySymbol(compCurrency)} {row.avgTransactionNetSales.toFixed(2)}</td>}
                    {visibleColumns.netSalesIncTax && <td>{getCurrencySymbol(compCurrency)} {row.netSalesIncTax.toFixed(2)}</td>}
                    {visibleColumns.netSalesExcTax && <td>{getCurrencySymbol(compCurrency)} {row.netSalesExcTax.toFixed(2)}</td>}
                    {visibleColumns.refundQty && <td>{row.refundQty}</td>}
                    {visibleColumns.refundValue && <td>{getCurrencySymbol(compCurrency)} {row.refundValue.toFixed(2)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Component */}
          <Pagination
            totalRecords={totalRecords}
            pageSize={pageSize}
            pageNumber={pageNumber}
            totalPages={totalPages}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            themeColor={themeColor}
          />
        </div>
      </div>
    </div>
  );
};

export default SalesBreakdownByTime;

